import React from 'react';
import I18n from 'modules/i18n';
import { LinkButton } from '@/components/ui/Buttons';
import { FilterCategory } from '@/components/common/libraries/common/Filter';
import Checkbox from '@/components/common/libraries/common/Checkbox';
import { TagType, UnitFilterOption } from '@/components/shared/Correlations/types';
import LessonFilter from './LessonFilter/LessonFilter';
import SectionHeading from './SectionHeading';
import StandardsFilter from './StandardsFilter/StandardsFilter';
import useCorrelationsStore from './useCorrelationsStore';
import useFilterData from './useFilterData';

interface FiltersProps {
  filterData: TagType[];
  grade: string | number | null;
  lessonOptions?: UnitFilterOption[];
  programId: number;
  showStandards?: boolean;
  stateId: number | null;
}

// With `lessonOptions` (correlations_filters on) the legacy page-derived filter must never run.
const NO_TAGS: TagType[] = [];

type FilterType = {
  value: number;
  label: string;
  checked: boolean;
};

const Filters = ({ filterData, grade, lessonOptions, programId, showStandards = false, stateId }: FiltersProps) => {
  const { filters, setFilters } = useFilterData({ filterData: lessonOptions ? NO_TAGS : filterData });
  const clearLessons = useCorrelationsStore(s => s.clearLessons);
  const clearStandards = useCorrelationsStore(s => s.clearStandards);
  const someChecked = (type: string) => filters[type].some((filter: FilterType) => filter.checked);
  const allChecked = (type: string) => filters[type].every((filter: FilterType) => filter.checked);

  const handleFilterChange = (type: string, value: string | number, checked: boolean) => {
    const oldFilters = filters[type];

    const newFilters = oldFilters.map(filter => (
      filter.value === value ? { ...filter, checked } : filter
    ));

    setFilters({ ...filters, [type]: newFilters });
  };

  const setAllFilters = (type: string, checked: boolean) => {
    const newFilters = filters[type].map(filter => ({ ...filter, checked }));

    setFilters({ ...filters, [type]: newFilters });
  };

  const clearAllFilters = () => {
    const newFilters = Object.keys(filters).reduce((acc, key) => {
      acc[key] = filters[key].map((filter: FilterType) => ({ ...filter, checked: false }));

      return acc;
    }, {});

    setFilters(newFilters);
    clearLessons();
    clearStandards();
  };

  const sortedFilters = () => (
    Object.keys(filters).sort((a, b) => {
      // match digits after U, as all strings are in the format U<number>
      const matcher = /^U(\d+)/;

      const matchA = a.match(matcher);
      const matchB = b.match(matcher);

      const numA = matchA ? parseInt(matchA[1], 10) : 0;
      const numB = matchB ? parseInt(matchB[1], 10) : 0;

      return numA - numB;
    })
  );

  const hasUnitFilters = lessonOptions ? lessonOptions.length > 0 : sortedFilters().length > 0;

  // AIDEV-NOTE: the rail is `tw-sticky tw-h-0`, so the page can't scroll anything below the viewport edge
  // into view. Under correlations_filters each section caps itself at a fixed height (Figma: Unit 210px,
  // Standards 215px) instead; with the flag off the Unit list stays uncapped, as it was before the flag.
  return (
    <div className="tw-sticky tw-top-1 tw-h-0 tw-flex-grow-0 tw-flex-shrink-0 tw-basis-60">
      <div className="card tw-mr-3 tw-mt-3">
        <h2 className="tw-px-4 tw-mt-3 heading4">{I18n.t('filters')}</h2>
        <LinkButton
          className="tw-absolute tw-right-1 tw-top-5 tw-text-sm tw-cursor-pointer"
          onClick={clearAllFilters}
        >
          {I18n.t('clear')}
        </LinkButton>
        <hr className="tw-mx-2 tw-mt-1 tw-mb-2" />
        {lessonOptions && hasUnitFilters && <LessonFilter units={lessonOptions} />}
        {!lessonOptions && hasUnitFilters && (
          <FilterCategory
            className="tw-pr-4 tw-mb-1"
            filterClassName="tw-pl-4"
            shouldCollapse={false}
            triggerElement={<SectionHeading>{I18n.t('unit')}</SectionHeading>}
          >
            <div
              aria-label={I18n.t('unit')}
              className={showStandards ? 'tw-max-h-[210px] tw-overflow-y-auto' : undefined}
              role="group"
            >
              {sortedFilters().map(filter => (
                <FilterCategory
                  key={filter}
                  shouldCollapse
                  caret="row"
                  filterClassName="tw-pl-8"
                  filters={filters[filter]}
                  triggerElement={(
                    <Checkbox
                      checked={allChecked(filter)}
                      extraClassName="tw-pb-2 tw-pl-4 tw-pr-4"
                      extraInputClassName="tw-mt-0.5"
                      indeterminate={someChecked(filter) && !allChecked(filter)}
                      label={filter}
                      onChange={(event) => {
                        setAllFilters(filter, event.target.checked);
                      }}
                    />
                  )}
                  onChange={(value, checked) => {
                    handleFilterChange(filter, value, checked);
                  }}
                />
              ))}
            </div>
          </FilterCategory>
        )}
        {showStandards && stateId != null && (
          <>
            {hasUnitFilters && <hr className="tw-mx-2 tw-my-2" />}
            <StandardsFilter grade={grade} programId={programId} stateId={stateId} />
          </>
        )}
      </div>
    </div>
  );
};

export default Filters;
